import React, { useContext } from 'react';

import { ViewPageGateway } from '@confluence/view-page-gateway';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { VIEW_PAGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getStatusCodeError } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';

import { type ContentPrerequisitesProps, ContentPrerequisites } from './ContentPrerequisites.tsx';

type PagePrerequisitesProps = Pick<
	ContentPrerequisitesProps,
	'children' | 'contentQueryResult' | 'route' | 'versionOverride'
>;

export const PagePrerequisites = ({
	children,
	contentQueryResult,
	route,
	versionOverride,
}: PagePrerequisitesProps): React.JSX.Element => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const handleTimeoutError = (error: Error) => {
		const isTimeoutError = error?.toString().includes('SocketTimeoutException');
		if (isTimeoutError) {
			experienceTracker.fail({
				name: VIEW_PAGE_EXPERIENCE,
				error,
			});
		} else {
			experienceTracker.abort({
				name: VIEW_PAGE_EXPERIENCE,
				reason: error.message,
			});
		}
		markErrorAsHandled(error);
	};

	const handleUnexpectedError = (error: Error) => {
		const isUserError = getStatusCodeError(error)?.startsWith('4');

		if (isUserError) {
			experienceTracker.abort({
				name: VIEW_PAGE_EXPERIENCE,
				reason: error.message,
			});
		} else {
			experienceTracker.fail({
				name: VIEW_PAGE_EXPERIENCE,
				error,
			});
		}
		markErrorAsHandled(error);

		throw error; // Throw to propagate to error boundary - otherwise just shows a blank screen
	};
	// When unauthorized and viewing a restricted page, user is prompted
	// with SuperAdmin gateway
	return (
		<ViewPageGateway>
			<ContentPrerequisites
				route={route}
				contentQueryResult={contentQueryResult}
				versionOverride={versionOverride}
				handleUnexpectedError={handleUnexpectedError}
				handleTimeoutError={handleTimeoutError}
			>
				{children}
			</ContentPrerequisites>
		</ViewPageGateway>
	);
};
